// People grouping and filtering helpers.
// All are el.call()-compatible (regular functions, `this` binds to element).

import { groupByRole } from './roleHelpers.js'
import { LEGACY_ORG_IDS, effectiveCategory, CATEGORIES } from './permissions.js'
import { personTeams } from '@symbo-ls/workspace-shared/functions/teams.js'

// 'Other' is the teamless bucket key — labelled 'No team' so the
// default grouped view reads as what it is (people without a
// TeamMember row in the active org) instead of a mystery category.
const GROUP_LABEL = { Other: 'workspace.people.group.noTeam' }

export function groupLabel(raw) {
  // An empty group is the teamless bucket too — its label is the bundle key,
  // never the bare id.
  const key = raw || 'Other'
  return GROUP_LABEL[key] || key.replace(/^./, (c) => c.toUpperCase())
}

// REAL teams only — system permission groups (Admin / Manager /
// Members / HR / … seeded per org by the server's SystemTeamsSeeder
// as permission carriers) are NOT teams in the people-directory
// sense; everyone is in `Members`, so treating them as teams turned
// the grouping into a permissions readout.
// Accepts a person-like `{ userId, email }` (preferred — userId resolves for
// non-mgmt viewers whose rows carry no email) or a bare email string (legacy).
// Delegates the id-first/email-fallback join to teams.js personTeams.
const _realTeams = (personOrEmail, root) =>
  personTeams(personOrEmail, root).filter((t) => !t.isSystem)

// Resolve a person's primary REAL team name via the org-SCOPED,
// deduped team list (teams.js personTeams) minus system permission
// groups. Returns 'Other' for people with no real team membership in
// the active org.
export function groupKeyOf(person, root) {
  const teams = _realTeams(person, root)
  return teams[0]?.name || 'Other'
}

// Clip `public.people` (cross-tenant) down to the active org's roster.
// NEVER return unfiltered people for a known active org — that leaks
// cross-tenant users. Legacy orgs bypass via LEGACY_ORG_IDS.
//
// `orgMembersByOrgId[orgId]` now comes back PRESENT-BUT-EMPTY (`{}`),
// not just possibly-missing, for a non-mgmt (plain member/contributor)
// viewer as of tickets/fable.md P0/P1 SECURITY (server roster-read
// projection — GET /organizations/:orgId/members no longer returns peer
// emails to a non-mgmt caller, and `functions/teams.js` builds this map
// by extracting `m.user.email` off that response, so it comes back
// legitimately empty for them, every time, not just during the boot
// race this function was originally guarding). `{}` is truthy, so the
// old `if (!set)` trigger did NOT catch it — it fell through to
// `people.filter(p => !!set[...])` with a permanently-empty `set`,
// matching NOBODY, not even the viewer themselves (worse than the
// existing self-only fallback below, which this file's own
// `peopleGroups.orgScoping.test.js` pins as the deliberate, tested
// contract for "no usable member set" — kept AS-IS here, not replaced;
// only the trigger condition is widened to also catch "present but
// empty", not just "missing").
//
// This intentionally does NOT fully resolve the new permanent case for
// non-mgmt viewers — self-only means their /people list narrows to just
// themselves rather than the full roster at reduced (name-only)
// per-row detail, which is safe but narrower than the ticket's
// "projection, not access, split" intent. The clean fix is re-keying
// this map (and `teamsByEmail`) off `userId` instead of `email` — every
// entry already carries `userId`/`user._id` even in the reduced
// projection — but that's a wider client refactor across every consumer
// of these maps, out of scope for the server-side security pass that
// surfaced this. Flagged for a follow-up ticket.
//
// THE REASON IS RESOLVED FIRST, AND IT IS THE SAME ONE THE PAGE ANNOUNCES.
// `peopleRosterScope` names WHY the roster has the size it has — four
// positively computed outcomes, never "the list happens to be length 1":
//   'all'      no active org, or a legacy org  -> the unclipped list
//   'org'      the org's member set is readable -> the real roster
//   'selfOnly' the member set is missing or present-but-empty (the non-mgmt
//              projection described above) AND the viewer is known
//              -> the viewer alone
//   'none'     the member set is unusable and the viewer is unknown -> nobody
// /people's reduced-roster notice reads this same resolver, so the notice and
// the list can never disagree about why the list is short — the identical
// discipline that collapsed filterPeople and peopleMemberStatusSummary onto
// one scoping function (PEOPLE-STATS-1).
export const peopleRosterScope = (root) => {
  const orgId = root?.jwtClaims?.workspace_org_id
  if (!orgId) return 'all'
  if (LEGACY_ORG_IDS.has(orgId)) return 'all'
  const set = root?.orgMembersByOrgId?.[orgId]
  if (set && Object.keys(set).length > 0) return 'org'
  return String(root?.currentUserEmail || '').toLowerCase() ? 'selfOnly' : 'none'
}

const _activeOrgPeople = (root) => {
  const people = root?.people || []
  const scope = peopleRosterScope(root)
  if (scope === 'all') return people
  if (scope === 'none') return []
  if (scope === 'selfOnly') {
    const myEmail = String(root?.currentUserEmail || '').toLowerCase()
    return people.filter((p) => String(p?.email || '').toLowerCase() === myEmail)
  }
  const set = root.orgMembersByOrgId[root.jwtClaims.workspace_org_id]
  return people.filter((p) => !!set[String(p?.email || '').toLowerCase()])
}

// el.call('peopleRosterIsReduced') — TRUE when the viewer sees a roster that
// permission cut down to themselves, not a workspace that is genuinely
// almost empty. /people renders its notice off this flag, so a one-person
// workspace with a readable member set stays silent.
export function peopleRosterIsReduced() {
  const root = this && this.state && this.state.root
  return peopleRosterScope(root) === 'selfOnly'
}

// el.call('peopleFilterActive') — the ONE filter predicate. /people's
// filtered-empty message and its fresh-empty message are the same question
// asked twice; two inline copies of this expression would drift the day a
// fifth filter lands.
export function peopleFilterActive() {
  const root = this && this.state && this.state.root
  return !!(
    (root?.peopleQuery || '').trim() ||
    root?.selectedGroup ||
    root?.selectedRole ||
    root?.selectedTeamCategory
  )
}

// Returns all team categories with user counts.
// el.call('peopleGroupCategories')
export function peopleGroupCategories() {
  const root = this && this.state && this.state.root
  const people = _activeOrgPeople(root)
  const counts = new Map()
  for (const p of people) {
    const k = groupKeyOf(p, root)
    counts.set(k, (counts.get(k) || 0) + 1)
  }
  return [...counts.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([key, count]) => ({ key, label: groupLabel(key), count, name: key, kind: 'group' }))
}

// Returns all roles with user counts derived from root.roles.byUser.
// CU-10 §90: roles no longer on person object; source is root.roles.byUser.
// el.call('peopleRoleCategories')
// CU-10 §90: public.roles view dropped (migration 0087). Role order/labels
// fall back to the canonical hardcoded list; counts derived from byUser.
const ROLE_FALLBACK_ORDER = ['superadmin', 'admin', 'manager', 'member', 'advisor', 'investor']
const ROLE_FALLBACK_LABELS = {
  superadmin: 'Superadmin',
  admin: 'Admin',
  manager: 'Manager',
  member: 'Member',
  advisor: 'Advisor',
  investor: 'Investor'
}

export function peopleRoleCategories() {
  const root = this && this.state && this.state.root
  const people = _activeOrgPeople(root)
  const byUser = root?.roles?.byUser || {}
  const counts = new Map()
  for (const p of people) {
    // Legacy fallback for pre-§90 data still in p.roles
    const legacyRoles = Array.isArray(p.roles) && p.roles.length > 0 ? p.roles : null
    if (legacyRoles) {
      for (const r of legacyRoles) counts.set(r, (counts.get(r) || 0) + 1)
    } else {
      const role = byUser[p.userId] || 'member'
      counts.set(role, (counts.get(role) || 0) + 1)
    }
  }
  return ROLE_FALLBACK_ORDER.filter((r) => counts.has(r)).map((r) => ({
    key: r,
    label: ROLE_FALLBACK_LABELS[r] || r.replace(/^./, (c) => c.toUpperCase()),
    count: counts.get(r) || 0,
    name: r,
    kind: 'role'
  }))
}

// Member status — the simple management lens (2026-06-06 per Nika):
// a member is ACTIVE when they belong to ≥1 active-status team in the
// org (Members is active, so every reconciled org member is active by
// default), INACTIVE otherwise (only-inactive teams, or none). Status
// is therefore managed ON TEAMS: flip a team's category / move people
// between teams and their member status follows.
export function memberStatusOf(email, root) {
  // A per-person status of 'left' (departed) or 'inactive' (read-only access)
  // wins over the team-derived status — the member is inactive regardless of
  // which teams they're in.
  const person = ((root && root.people) || []).find(
    (p) => (p.email || '').toLowerCase() === (email || '').toLowerCase()
  )
  if (person && (person.status === 'left' || person.status === 'inactive')) return 'inactive'
  // Prefer the resolved person (carries userId) so non-mgmt viewers resolve;
  // fall back to the bare email when no roster row was found.
  const teams = personTeams(person || email, root)
  return teams.some((t) => isActiveTeamCategory(t.category)) ? 'active' : 'inactive'
}

// Members summary for the /team-members rail: Everyone (total) /
// Active / Inactive with counts. Replaces the old 5-category filter
// list. el.call('peopleMemberStatusSummary')
export function peopleMemberStatusSummary() {
  const root = this && this.state && this.state.root
  const people = _activeOrgPeople(root)
  let active = 0
  for (const p of people) {
    if (memberStatusOf(p?.email, root) === 'active') active += 1
  }
  return [
    {
      key: 'all',
      label: 'workspace.people.filter.everyone',
      count: people.length,
      name: null,
      kind: 'category'
    },
    {
      key: 'active',
      label: 'workspace.people.filter.active',
      count: active,
      name: 'active',
      kind: 'category'
    },
    {
      key: 'inactive',
      label: 'workspace.people.filter.inactive',
      count: people.length - active,
      name: 'inactive',
      kind: 'category'
    }
  ]
}

// (Legacy 5-category rail options — superseded by
// peopleMemberStatusSummary on /team-members; kept for /admin
// consumers that still group by the full enum.)
export function peopleTeamCategories() {
  const root = this && this.state && this.state.root
  const teamsByEmail = root?.teamsByEmail || {}
  // id-first — non-mgmt roster rows carry no email (server projection), so
  // the email map never matches for them (tickets/fable.md P0 regression).
  const teamsByUserId = root?.teamsByUserId || {}
  const counts = Object.fromEntries(CATEGORIES.map((c) => [c, 0]))
  counts.none = 0
  for (const p of _activeOrgPeople(root)) {
    const byId = p?.userId != null ? teamsByUserId[String(p.userId)] : null
    const email = String(p?.email || '').toLowerCase()
    const cat = effectiveCategory(
      (Array.isArray(byId) && byId.length && byId) || teamsByEmail[email]
    )
    counts[cat] = (counts[cat] || 0) + 1
  }
  // Bundle KEYS — every consumer of these rows resolves through polyglot at
  // its render site (PeopleFilterLink / PeopleGroupBlock), which also lets a
  // real TEAM name pass through untouched.
  const LABELS = {
    manager: 'workspace.people.category.manager',
    active: 'workspace.people.filter.active',
    passive: 'workspace.people.category.passive',
    external: 'workspace.people.category.external',
    archived: 'workspace.people.category.archived',
    none: 'workspace.people.category.unassigned'
  }
  const out = []
  for (const cat of CATEGORIES) {
    if (counts[cat] > 0) {
      out.push({ key: cat, label: LABELS[cat], count: counts[cat], name: cat, kind: 'category' })
    }
  }
  if (counts.none > 0) {
    out.push({
      key: 'none',
      label: 'workspace.people.category.unassigned',
      count: counts.none,
      name: 'none',
      kind: 'category'
    })
  }
  return out
}

// §unified-perms PR #4 (2026-05-23): /admin dashboard groups people by
// their DOMINANT team category (precedence per CATEGORIES order). One
// bucket per category with at least one member; archived hidden by
// spec. Use as the default `el.call('groupPeopleByCategory')` on
// /admin. Differs from groupFilteredPeople (which groups by team name)
// in that the bucket is the CATEGORY, not the team name.
export function groupPeopleByCategory() {
  const root = this && this.state && this.state.root
  const filtered = filterPeople.call(this)
  const teamsByEmail = root?.teamsByEmail || {}
  // id-first, email fallback — same P0-regression join as peopleTeamCategories.
  const teamsByUserId = root?.teamsByUserId || {}
  const buckets = new Map()
  for (const p of filtered) {
    const byId = p?.userId != null ? teamsByUserId[String(p.userId)] : null
    const email = String(p?.email || '').toLowerCase()
    const cat = effectiveCategory(
      (Array.isArray(byId) && byId.length && byId) || teamsByEmail[email]
    )
    if (!buckets.has(cat)) buckets.set(cat, [])
    buckets.get(cat).push(p)
  }
  // Bundle KEYS — every consumer of these rows resolves through polyglot at
  // its render site (PeopleFilterLink / PeopleGroupBlock), which also lets a
  // real TEAM name pass through untouched.
  const LABELS = {
    manager: 'workspace.people.category.manager',
    active: 'workspace.people.filter.active',
    passive: 'workspace.people.category.passive',
    external: 'workspace.people.category.external',
    archived: 'workspace.people.category.archived',
    none: 'workspace.people.category.unassigned'
  }
  // Order: manager > active > passive > external > archived > none
  const order = [...CATEGORIES, 'none']
  return order
    .filter((cat) => cat !== 'archived' && buckets.has(cat) && buckets.get(cat).length > 0)
    .map((cat) => ({ name: LABELS[cat] || cat, people: buckets.get(cat) }))
}

// Teams directory for the /people Teams section. Sourced from the DB:
// `root.teams` is loaded by loadAllTeams from Mongo's Team/TeamMember
// via the SDK (see workspace/functions/teams.js) — NOT derived from
// per-person profile fields. Scoped to the active org; member emails
// and counts come from the same SDK-sourced `teamsByEmail` map the
// grouping helpers use. Includes teams with zero members (they exist
// in the DB but never show up in membership-derived buckets).
// el.call('peopleTeamsDirectory')
export function peopleTeamsDirectory() {
  const root = this && this.state && this.state.root
  const orgId = root?.jwtClaims?.workspace_org_id
  // ALL of the active org's teams (2026-06-06 per Nika "it does not
  // show all teams") — system teams (Members / Admin / HR / …)
  // included, since they carry real memberships worth browsing
  // (Members holds the whole roster). The DEFAULT body grouping still
  // uses real teams only (see groupFilteredPeople) so the system
  // groups don't swallow the directory; clicking a system team in the
  // rail filters to its members explicitly.
  const teams = (root?.teams || []).filter((t) => !orgId || String(t.orgId) === String(orgId))
  // Count only members that appear in the active-org PEOPLE roster
  // (_activeOrgPeople — the federated, profiled members /people renders).
  // Invited-but-never-onboarded members (rows in teamsByEmail with no
  // people-roster entry — dropped by /core/users/people's supabaseUserId
  // gate) are intentionally excluded here so the rail's counts match the
  // body list + Members summary (no more "Members 85" beside "Active 53").
  // The FULL membership, including those invitees, lives on /admin/teams
  // (2026-06-09 per Nika).
  const rosterEmails = new Set(
    _activeOrgPeople(root).map((p) => String(p?.email || '').toLowerCase())
  )
  const membersByTeam = {}
  for (const [email, list] of Object.entries(root?.teamsByEmail || {})) {
    if (!rosterEmails.has(email)) continue
    for (const t of list || []) {
      if (!membersByTeam[t.id]) membersByTeam[t.id] = []
      membersByTeam[t.id].push(email)
    }
  }
  return teams
    .map((t) => ({
      id: t.id,
      name: t.name,
      slug: t.slug,
      orgName: t.orgName,
      // Team category (manager/active/passive/external/archived) —
      // the rail splits on it: Active vs Inactive sections.
      category: t.category || 'active',
      emails: membersByTeam[t.id] || [],
      count: (membersByTeam[t.id] || []).length,
      kind: 'team'
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
}

// Category split for the /team-members rail: 'manager' + 'active'
// teams are Active; passive / external / archived are Inactive.
export const isActiveTeamCategory = (category) => category === 'active' || category === 'manager'

// The per-person filter predicate, built ONCE per call and shared by
// `filterPeople` and `peopleHasVisible`, so "who passes the filter" has one
// definition. Two copies would drift the day a fifth filter lands — the same
// reason `peopleFilterActive` exists.
const _personMatcher = (root) => {
  const g = root?.selectedGroup
  const r = root?.selectedRole
  const c = root?.selectedTeamCategory // 'active' | 'inactive' | null (binary member-status lens)
  const q = (root?.peopleQuery || '').trim().toLowerCase()
  // CU-10 §90: role per person from root.roles.byUser (not p.roles)
  const byUser = root?.roles?.byUser || {}
  return (p) => {
    // Team filter matches ANY of the person's org-scoped teams —
    // system ones included so the rail's Members / HR / … entries
    // filter correctly. 'Other' still means "no REAL team" to match
    // the default grouping's bucket.
    if (g) {
      const all = personTeams(p, root).map((t) => t.name)
      const real = _realTeams(p, root)
      const mine = all.length || real.length ? all : []
      if (!real.length) mine.push('Other')
      if (!mine.includes(g)) return false
    }
    if (r) {
      // Legacy fallback: person still has .roles array
      const legacyRoles = Array.isArray(p.roles) && p.roles.length > 0 ? p.roles : null
      if (legacyRoles) {
        if (legacyRoles.length === 0 && r !== 'member') return false
        if (legacyRoles.length > 0 && !legacyRoles.includes(r)) return false
      } else {
        const assignedRole = byUser[p.userId] || 'member'
        if (assignedRole !== r) return false
      }
    }
    if (c) {
      // Binary member-status lens (active | inactive) — derived from
      // team membership; see memberStatusOf.
      if (memberStatusOf(p?.email, root) !== c) return false
    }
    if (q) {
      const name = (p?.name || '').toLowerCase()
      const email = (p?.email || '').toLowerCase()
      const position = (p?.position || '').toLowerCase()
      if (!name.includes(q) && !email.includes(q) && !position.includes(q)) return false
    }
    return true
  }
}

// Filter people by active-org membership + selected group and/or role.
// `public.people` is a cross-tenant view (user_profiles has no
// workspace_id); scoping down to the active org's member set is how
// tenancy gets enforced for identity surfaces. `orgMembersByOrgId` is
// populated by `loadAllTeams` on sign-in. Legacy orgs bypass the
// OrgMember intersection (see LEGACY_ORG_IDS).
//
// Scoping delegates to `_activeOrgPeople` (below) rather than
// re-implementing the org-membership-set lookup — the two used to diverge
// on what to do while `orgMembersByOrgId[activeOrgId]` hasn't loaded yet:
// this function treated "not loaded" as "show everyone" while
// `_activeOrgPeople` (source of the /people MEMBERS stat block) treated it
// as "show only me". Same loading moment, two different counts on two
// surfaces of the same page — see tickets/sonnet.md PEOPLE-STATS-1. One
// scoping function now, so every consumer settles on the identical roster.
// el.call('filterPeople')
export function filterPeople() {
  const root = this && this.state && this.state.root
  return _activeOrgPeople(root).filter(_personMatcher(root))
}

// el.call('peopleHasVisible') — does ANY person pass the active filter?
// It stops at the first match and builds nothing. Every branch of
// `groupFilteredPeople` puts each filtered person into at least one group, so
// "some group has a person" and "some person passes the filter" are the same
// fact (pinned against the real grouper, all four branches, in
// __tests__/peopleRosterStates.test.js). A paint gate asks THIS; only the
// list body pays for the groups.
export function peopleHasVisible() {
  const root = this && this.state && this.state.root
  return _activeOrgPeople(root).some(_personMatcher(root))
}

// el.call('peopleRosterView') — WHICH of /people's five body blocks paints.
//   'loading' | 'error'   straight from `peopleStatus` (homeLoadState.js),
//                          the resolver Home's widgets ask
//   'list'                 loaded, and somebody passes the filter
//   'emptyFiltered'        loaded, nobody passes, a filter is active
//   'emptyFresh'           loaded, nobody at all, no filter
// ONE resolver, so the five blocks stay mutually exclusive by construction.
// Each block carried its own copy of this decision before, in three
// spellings, and the copies had already started to differ.
export function peopleRosterView() {
  const root = this && this.state && this.state.root
  const status = this.call('peopleStatus', root)
  if (status !== 'loaded') return status
  if (peopleHasVisible.call(this)) return 'list'
  return peopleFilterActive.call(this) ? 'emptyFiltered' : 'emptyFresh'
}

// Group filtered people by team (default) or by active filter. When a
// role or category filter is active, shows a single group. Team-first
// model replaces the legacy role-first grouping (I11).
// el.call('groupFilteredPeople')
export function groupFilteredPeople() {
  const root = this && this.state && this.state.root
  const filtered = filterPeople.call(this)

  // Role filter active — single group labelled with the role
  if (root?.selectedRole) {
    const available = root?.roles?.available || []
    const meta = available.find((r) => r.name === root.selectedRole)
    return [{ name: meta?.label || root.selectedRole, people: filtered }]
  }

  // Category filter active — single group labelled with the category
  if (root?.selectedTeamCategory) {
    // The header carries the same bundle KEY the rail row carried, so the
    // filtered view and the filter that produced it read identically.
    const CATEGORY_KEY = {
      manager: 'workspace.people.category.manager',
      active: 'workspace.people.filter.active',
      passive: 'workspace.people.category.passive',
      external: 'workspace.people.category.external',
      archived: 'workspace.people.category.archived',
      inactive: 'workspace.people.filter.inactive',
      none: 'workspace.people.category.unassigned'
    }
    const label =
      CATEGORY_KEY[root.selectedTeamCategory] ||
      root.selectedTeamCategory.charAt(0).toUpperCase() + root.selectedTeamCategory.slice(1)
    return [{ name: label, people: filtered }]
  }

  // Team filter active — single group labelled with the team. Without
  // this, filtering by a SYSTEM team (Members / HR / …) fell through
  // to the real-team bucketing below and the header read "No team"
  // for people whose only membership is the selected system team.
  //
  // Scoped to the active-org PEOPLE roster only. Invited-but-never-
  // onboarded members (TeamMember rows with no people-roster entry) are
  // intentionally NOT surfaced on /people — the full team membership,
  // including those invitees (with a Reinvite affordance), lives on
  // /admin/teams (2026-06-09 per Nika). The previous ghost-card
  // injection here is what made /people inconsistent with its own counts.
  if (root?.selectedGroup) {
    // Stamp `_groupTeamName` so each PersonCard's team badge reflects the
    // section it's listed under (the selected group), not the member's
    // arbitrary primary team — see the default branch below for why.
    const label = groupLabel(root.selectedGroup)
    const tagged = filtered.map((p) => ({ ...p, _groupTeamName: label }))
    return [{ name: label, people: tagged }]
  }

  // Default (including explicit team filter) — group by REAL team
  // (system permission groups excluded). A person appears under EVERY
  // org-scoped team they belong to (true group-by-teams semantics;
  // the previous primary-team-only bucketing hid multi-team
  // membership), and under 'No team' (key 'Other') when they have
  // none in the active org.
  //
  // Each bucket gets a SHALLOW COPY of the person stamped with
  // `_groupTeamName` = the team name of THIS section. A multi-team
  // member appears in several buckets; without the per-section stamp,
  // every one of their cards read the same primary-team badge
  // (`personTeams(...)[0].name`) regardless of section — so under
  // "Class B" a member also in "Class C" was mislabelled "Class C".
  // Pushing the shared reference and stamping in place would lose to
  // last-write-wins; the copy keeps each section's stamp distinct.
  const buckets = new Map()
  for (const p of filtered) {
    const teams = _realTeams(p, root)
    const keys = teams.length ? teams.map((t) => t.name) : ['Other']
    for (const k of keys) {
      if (!buckets.has(k)) buckets.set(k, [])
      buckets.get(k).push({ ...p, _groupTeamName: k === 'Other' ? null : k })
    }
  }
  // Sort: named teams alphabetically first, then "No team" last
  return [...buckets.entries()]
    .sort((a, b) => {
      if (a[0] === 'Other') return 1
      if (b[0] === 'Other') return -1
      return a[0].localeCompare(b[0])
    })
    .map(([key, ppl]) => ({ name: groupLabel(key), people: ppl }))
}
